import React from 'react';
import { AppIdea } from '@/hooks/app-library';
import GradientButton from '@/components/ui/GradientButton';
import { CloudDownload, Trash2, Loader2 } from 'lucide-react';
import { useUser } from '@/hooks/auth/use-user';
import { useAppIdeaPDF } from '@/hooks/pdf';

interface AppIdeaActionButtonsProps {
  idea: AppIdea;
  onDelete?: (idea: AppIdea) => Promise<void>;
  onDownload?: (idea: AppIdea) => void;
}

export const AppIdeaActionButtons: React.FC<AppIdeaActionButtonsProps> = ({ 
  idea, 
  onDelete, 
  onDownload 
}) => {
  const { user } = useUser();
  const { generatePDF, isGenerating } = useAppIdeaPDF();
  
  // Check if user owns this app idea or is admin
  const canDelete = user && (idea.user_id === user.id || user.email?.includes('@admin'));

  const handleDownload = () => {
    if (onDownload) {
      onDownload(idea);
    } else {
      // Generate PDF instead of markdown
      generatePDF(idea);
    }
  };

  const handleDelete = async () => {
    if (onDelete && canDelete) {
      await onDelete(idea);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Download Button */}
      <GradientButton
        variant="blue"
        icon={isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudDownload className="h-4 w-4" />}
        onClick={handleDownload}
        disabled={isGenerating}
        testId="download-app-idea-button"
        fullWidth
      >
        {isGenerating ? 'Generating PDF...' : 'Download PDF'}
      </GradientButton>

      {/* Delete Button - Only show if user can delete */}
      {canDelete && onDelete && (
        <GradientButton
          variant="red"
          icon={<Trash2 className="h-4 w-4" />}
          onClick={handleDelete}
          testId="delete-app-idea-button"
          fullWidth
        >
          Delete
        </GradientButton>
      )}
    </div>
  );
};
